import { config } from '../../utils/utils';
import store from '../../store';

export interface IndividualAdDocument {
  id: string;
  advertiseId: string;
  documentType: 'title_deed' | 'individual_id';
  assetId: string;
  uploadedBy: string;
  asset?: {
    id: string;
    fileName: string;
    fileType: string;
    key: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateIndividualAdDocumentRequest {
  advertiseId: string;
  documentType: 'title_deed' | 'individual_id';
  assetId: string;
}

/**
 * Create an individual ad document
 * @param data Document data
 * @returns Promise with created document
 */
export const createIndividualAdDocument = async (
  data: CreateIndividualAdDocumentRequest
): Promise<IndividualAdDocument> => {
  try {
    const { accessToken } = store.getState().auth;

    const url = `${config.BASE_URL}/individual-ad-documents`;

    const result = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!result.ok) {
      const errorData = await result.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create document');
    }

    return await result.json();
  } catch (error) {
    console.error('Error creating document:', error);
    throw error;
  }
};

/**
 * Get individual ad documents
 * @param options Optional parameters including filters
 * @returns Promise with documents data
 */
export const getIndividualAdDocuments = async (
  options?: {
    advertiseId?: string;
    documentType?: 'title_deed' | 'individual_id';
    skip?: number;
    limit?: number;
  }
): Promise<{ total: number; limit: number; skip: number; data: IndividualAdDocument[] }> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/individual-ad-documents`;
    const params = new URLSearchParams();

    if (options?.advertiseId) {
      params.append('advertiseId', options.advertiseId);
    }

    if (options?.documentType) {
      params.append('documentType', options.documentType);
    }

    if (options?.skip !== undefined) {
      params.append('skip', options.skip.toString());
    }

    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch documents');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

